/*
 * Keeping Tab inside a modal dialog.
 *
 * A dialog over the page (the photo viewer, the hub's lightbox) must not let
 * Tab wander into the page behind it: WAI-ARIA's dialog pattern, and the
 * rule AddIsolateModal and the hub's drawers already follow. This is that
 * rule as one function, so every dialog cycles the same way.
 */
export const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The elements inside `container` a Tab can reach, in document order. */
export function focusables(container) {
  if (!container) return [];
  return [...container.querySelectorAll(FOCUSABLE)]
    .filter((el) => !el.closest('[hidden]') && el.getAttribute('aria-hidden') !== 'true');
}

/**
 * Handle one keydown: a Tab that would leave `container` is wrapped to its
 * other end instead. Returns true when the event was a Tab it handled.
 * Nothing happens for any other key, or with nothing focusable inside.
 *
 * `extra` names stops outside the container that belong in its cycle all the
 * same -- the Report an issue tab (components/issues/ReportIssueTab.js), so a
 * keyboard can report from inside any dialog. They come after the
 * container's own, and are skipped while hidden or not in the page.
 */
export function trapTab(container, e, { extra } = {}) {
  if (e.key !== 'Tab' || !container) return false;
  const items = focusables(container);
  const outside = (Array.isArray(extra) ? extra : [extra])
    .filter(el => el && el.isConnected !== false && !el.hidden && !el.closest?.('[hidden]'));
  if (outside.length) {
    // Tab order would leave for the page between the two: every step is ours.
    const cycle = [...items, ...outside];
    const at = cycle.indexOf(document.activeElement);
    let next;
    if (e.shiftKey) next = at <= 0 ? cycle[cycle.length - 1] : cycle[at - 1];
    else next = at === -1 || at === cycle.length - 1 ? cycle[0] : cycle[at + 1];
    e.preventDefault();
    next.focus();
    return true;
  }
  if (!items.length) return false;
  const first = items[0];
  const last = items[items.length - 1];
  const at = document.activeElement;
  if (e.shiftKey && (at === first || !container.contains(at))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (at === last || !container.contains(at))) {
    e.preventDefault();
    first.focus();
  }
  return true;
}
